import { Component, computed, DestroyRef, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { RouterModule } from '@angular/router'
import { OcModalComponent, OcModalFooterComponent } from 'otimus-library'
import { SidebarService } from 'src/app/services/sidebar.service'
import { InternationalizationService } from 'src/app/services/internationalization.service'
import { NavigationMenuProgram } from '../../entities/navigation-menu-item.entity'
import { SidebarGroupTitleComponent } from '../sidebar-group-title/sidebar-group-title.component'

const COLLAPSED_LIMIT = 3

@Component({
  selector: 'app-sidebar-favorites',
  imports: [RouterModule, SidebarGroupTitleComponent, OcModalComponent, OcModalFooterComponent],
  templateUrl: './sidebar-favorites.component.html',
  styleUrl: './sidebar-favorites.component.scss',
})
export class SidebarFavoritesComponent {
  private readonly sidebarService = inject(SidebarService)
  private readonly destroyRef = inject(DestroyRef)
  protected readonly translateService = inject(InternationalizationService)

  protected expanded = signal(false)
  protected pendingDelete = signal<NavigationMenuProgram | null>(null)
  protected deleting = signal(false)

  protected visiblePrograms = computed(() => {
    const all = this.sidebarService.favoritePrograms()
    return this.expanded() ? all : all.slice(0, COLLAPSED_LIMIT)
  })

  protected hasMore = computed(
    () => this.sidebarService.favoritePrograms().length > COLLAPSED_LIMIT
  )

  protected hasFavorites = computed(
    () => this.sidebarService.favoritePrograms().length > 0
  )

  protected toggleExpanded() {
    this.expanded.update((v) => !v)
  }

  protected requestDelete(
    event: MouseEvent,
    fav: NavigationMenuProgram,
    modal: OcModalComponent
  ): void {
    event.preventDefault()
    event.stopPropagation()
    this.pendingDelete.set(fav)
    modal.open()
  }

  protected confirmDelete(modal: OcModalComponent): void {
    const fav = this.pendingDelete()
    if (!fav || this.deleting()) {
      return
    }
    this.deleting.set(true)
    // Optimistic remove
    this.sidebarService.favoritePrograms.update((list) =>
      list.filter((f) => f.id !== fav.id)
    )
    this.sidebarService
      .deleteFavorite(fav.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.deleting.set(false)
          this.pendingDelete.set(null)
          modal.close()
        },
        error: () => {
          this.sidebarService.favoritePrograms.update((list) => [fav, ...list])
          this.deleting.set(false)
          this.pendingDelete.set(null)
          modal.close()
        },
      })
  }

  protected cancelDelete(modal: OcModalComponent): void {
    this.pendingDelete.set(null)
    modal.close()
  }
}
